import { useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGImage from '~/components/Image/SVGImage'
import BgPattern from '~/landing/components/BgPattern'
import MathRecipeItemDesktop from '~/landing/components/MathRecipeItemDesktop'
import MathRecipeItemMobile from '~/landing/components/MathRecipeItemMobile'
import { MathRecipeSteps } from '~/landing/consts/Landing.consts'
import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'

import { useLandingEntryRoute } from '~/landing/contexts/landingEntryRoute'
import StartAnalytics from '~/analytics/start/startAnalytics'
import Button from '~/components/Buttons/Button'

export default function MathRecipe({
  buttonClassName,
  onClick,
}: {
  buttonClassName?: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
}): React.ReactElement {
  const { t } = useTranslation('landing')
  const entryRoute = useLandingEntryRoute()
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.XL })

  const handleButtonClick = (): void => {
    StartAnalytics.logCtaClick('onboarding_cta', 'landing_section_4_cta')
  }

  return (
    <>
      <div className="flex flex-col justify-start items-center relative w-full h-full text-white mx-auto overflow-hidden bg-gradient-to-t from-purple-indigo to-purple-midnight">
        <BgPattern />

        <ResponsiveImage
          className="absolute min-w-196 w-full max-w-302 -ml-[1000px] mt-48 rotate-180"
          height={886}
          imageClassName="object-contain"
          srcKey="landing/pad-pattern.png"
          width={1378}
        />

        <div className="flex flex-col gap-12 xl:gap-0 2xl:gap-18 text-center justify-center items-center mt-12 xl:mt-18 z-10 w-full">
          <div className="max-w-sm xs:max-w-xl xl:max-w-4xl px-4">
            <h1 className="text-3xl xl:text-5xl font-extrabold mb-2">{t('mathRecipe.mathIsNotACode')}</h1>
            <p className="text-md text-blue-pastel mx-auto px-2">{t('mathRecipe.ourUsersSay')}</p>
          </div>
          <div className="relative flex justify-center items-center w-full md:max-w-screen-md  lg:max-w-screen-lg  xl:max-w-screen-xl 2xl:max-w-screen-2.5xl z-20 h-full xl:h-[550px] 2xl:h-[650px]">
            {!isMobile && (
              <div className="absolute flex justify-center items-center max-w-full">
                <ResponsiveImage
                  height={121}
                  imageClassName="object-contain"
                  srcKey="landing/mathRecipeSliderLS.png"
                  width={1600}
                />
                <div className="bg-purple h-2 w-147 lg:w-176 xl:w-226 2xl:w-9/12 2xl:ml-18 ml-12 absolute -mt-1 2xl:-mt-2.5" />
              </div>
            )}
            <div
              className={`flex ${
                isMobile
                  ? 'flex-col gap-6 items-center relative justify-center h-full py-12 -mt-18 mr-8'
                  : 'flex-row h-full'
              }  w-full pl-12 lg:pl-24 2xl:pl-28  `}
            >
              <SVGImage
                className={`h-[1000px] min-w-4 ${!isMobile ? 'hidden' : 'absolute mr-[370px] -mt-12'}`}
                height={320}
                imageClassName="object-contain z-1 -mt-12 h-[1200px]"
                srcKey="landing/verticalSlider.svg"
              />
              {MathRecipeSteps.map((step, index) =>
                isMobile ? (
                  <MathRecipeItemMobile
                    isLastItem={index === MathRecipeSteps.length - 1}
                    key={step.title}
                    props={step}
                  />
                ) : (
                  <MathRecipeItemDesktop
                    isLastItem={index === MathRecipeSteps.length - 1}
                    key={step.title}
                    props={step}
                  />
                ),
              )}
            </div>
          </div>
          <Button
            className={
              buttonClassName ||
              '!bg-green-lime !text-black border-4 rounded-full px-8 py-4 text-md !font-black mx-auto w-78 hover:scale-110 duration-100 z-10 my-16'
            }
            href={onClick ? undefined : entryRoute}
            onClick={onClick || handleButtonClick}
          >
            {t('landing:intro.startJourney')}
          </Button>
        </div>
      </div>
    </>
  )
}
